type RouteHandler = (params: Record<string, string>) => void;

interface Route {
  pattern: RegExp;
  paramNames: string[];
  handler: RouteHandler;
}

class Router {
  private routes: Route[] = [];
  private notFoundHandler: RouteHandler = () => { };
  private currentPathname = '';

  add(path: string, handler: RouteHandler): void {
    const paramNames: string[] = [];
    const pattern = path.replace(/:([^/]+)/g, (_, paramName) => {
      paramNames.push(paramName);
      return '([^/]+)';
    });

    this.routes.push({
      pattern: new RegExp(`^${pattern}$`),
      paramNames,
      handler,
    });
  }

  notFound(handler: RouteHandler): void {
    this.notFoundHandler = handler;
  }

  navigate(path: string, replace = false): void {
    const navigationApi = (window as any).navigation;
    if (navigationApi && typeof navigationApi.navigate === 'function') {
      navigationApi.navigate(path, replace ? { history: 'replace' } : undefined);
    } else {
      const url = new URL(path, location.origin);
      if (replace) {
        history.replaceState(null, '', path);
      } else {
        history.pushState(null, '', path);
      }
      if (url.pathname !== this.currentPathname) {
        this.resolve();
      }
    }
  }

  resolve(): void {
    const path = window.location.pathname;
    this.currentPathname = path;

    for (const route of this.routes) {
      const match = path.match(route.pattern);
      if (match) {
        const params: Record<string, string> = {};
        route.paramNames.forEach((name, index) => {
          params[name] = match[index + 1];
        });
        route.handler(params);
        return;
      }
    }

    this.notFoundHandler({});
  }

  start(): void {
    const navigationApi = (window as Window & { navigation?: { addEventListener: (type: string, listener: (event: any) => void) => void } }).navigation;

    if (navigationApi && typeof navigationApi.addEventListener === 'function') {
      navigationApi.addEventListener('navigate', (event: any) => {
        if (!event.canIntercept || event.downloadRequest !== null) return;
        const url = new URL(event.destination.url);
        if (url.origin !== location.origin) return;
        if (event.hashChange) return;
        event.intercept({ handler: () => { this.resolve(); } });
      });
    } else {
      window.addEventListener('popstate', () => {
        if (window.location.pathname !== this.currentPathname) {
          this.resolve();
        }
      });

      document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const anchor = target.closest('a[data-link]');
        if (anchor) {
          e.preventDefault();
          const href = anchor.getAttribute('href');
          if (href) this.navigate(href);
        }
      });
    }

    this.resolve();
  }
}

export const router = new Router();
